// Factory function pattern to avoid Webpack TDZ issues with class inheritance
// This uses BaseSuperAdminFactory instead of class inheritance

import { createBaseSuperAdmin, type IBaseSuperAdmin } from "../BaseSuperAdminFactory";
import type { DocumentType } from "le-coffre-resources/dist/SuperAdmin";
import type { QueryPayload } from "../../../types";
import type { IGetDocumentTypesparams, IPutDocumentTypesParams, IPostDocumentTypesParams } from "./DocumentTypes";
import {
	buildDocumentTypesCreatePayload,
	buildDocumentTypesUpdatePayload,
} from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

interface DocumentTypesInstance {
	get(q?: IGetDocumentTypesparams): Promise<DocumentType[]>;
	post(body: IPostDocumentTypesParams): Promise<DocumentType>;
	getByUid(uid: string, q?: QueryPayload): Promise<DocumentType>;
	put(uid: string, body: IPutDocumentTypesParams): Promise<DocumentType>;
}

// Module-level singleton instance
let instance: DocumentTypesInstance | null = null;
let baseSuperAdminInstance: IBaseSuperAdmin | null = null;

async function documentTypesGet(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string, q?: IGetDocumentTypesparams): Promise<DocumentType[]> {
	const url = new URL(baseUrl);
	if (q) baseSuperAdmin.appendJsonSearchParams(url, { q });
	try {
		return await baseSuperAdmin.getRequest<DocumentType[]>(url);
	} catch (err) {
		baseSuperAdmin.onError(err);
		return Promise.reject(err);
	}
}

async function documentTypesPost(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string, body: IPostDocumentTypesParams): Promise<DocumentType> {
	const url = new URL(baseUrl);
	try {
		return await baseSuperAdmin.postRequest<DocumentType>(url, buildDocumentTypesCreatePayload(body));
	} catch (err) {
		baseSuperAdmin.onError(err);
		return Promise.reject(err);
	}
}

async function documentTypesGetByUid(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string, uid: string, q?: QueryPayload): Promise<DocumentType> {
	const url = new URL(baseUrl.concat(`/${uid}`));
	if (q) baseSuperAdmin.appendJsonSearchParams(url, { q });
	try {
		return await baseSuperAdmin.getRequest<DocumentType>(url);
	} catch (err) {
		baseSuperAdmin.onError(err);
		return Promise.reject(err);
	}
}

async function documentTypesPut(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string, uid: string, body: IPutDocumentTypesParams): Promise<DocumentType> {
	const url = new URL(baseUrl.concat(`/${uid}`));
	try {
		return await baseSuperAdmin.putRequest<DocumentType>(url, buildDocumentTypesUpdatePayload(body));
	} catch (err) {
		baseSuperAdmin.onError(err);
		return Promise.reject(err);
	}
}

function createDocumentTypes(): DocumentTypesInstance {
	if (!baseSuperAdminInstance) {
		baseSuperAdminInstance = createBaseSuperAdmin();
	}
	const baseSuperAdmin = baseSuperAdminInstance;
	const baseUrl = baseSuperAdmin.namespaceUrl.concat("/document-types");
	return {
		get: (q?: IGetDocumentTypesparams) => documentTypesGet(baseSuperAdmin, baseUrl, q),
		post: (body: IPostDocumentTypesParams) => documentTypesPost(baseSuperAdmin, baseUrl, body),
		getByUid: (uid: string, q?: QueryPayload) => documentTypesGetByUid(baseSuperAdmin, baseUrl, uid, q),
		put: (uid: string, body: IPutDocumentTypesParams) => documentTypesPut(baseSuperAdmin, baseUrl, uid, body),
	};
}

export function getDocumentTypesInstance(): DocumentTypesInstance {
	if (!instance) {
		instance = createDocumentTypes();
	}
	return instance;
}
